import React, { useMemo, useState, useEffect, useId } from 'react';
import type { IODADataPoint } from '../types/ioda';
import { IDEAL_THRESHOLD, resolveOutageClock, digitColorClass, type OutageClockResolution } from '../utils/outageClockLogic';

function normalizeSeries(points: IODADataPoint[]): { from: number; value: number }[] {
  return points
    .filter((p) => p && Number.isFinite(p.from) && Number.isFinite(p.value))
    .map((p) => ({ from: p.from, value: Math.max(0, Math.min(100, p.value)) }))
    .sort((a, b) => a.from - b.from);
}

function splitDuration(ms: number): { d: number; h: number; m: number; s: number } {
  const sec = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return { d, h, m, s };
}

/** Inline segmented duration for fast scanning (LTR numerals) */
function formatInlineDuration(d: number, h: number, m: number, s: number): string {
  return `${d}d ${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

function MeritSparkline({ points }: { points: IODADataPoint[] }) {
  const clipId = useId().replace(/:/g, '');
  const norm = useMemo(() => normalizeSeries(points), [points]);
  const w = 120;
  const h = 48;
  const { lineD, areaD } = useMemo(() => {
    if (norm.length < 2) return { lineD: '', areaD: '' };
    const xs = norm.map((_, i) => (i / (norm.length - 1)) * w);
    const yAt = (v: number) => h - (Math.max(0, Math.min(100, v)) / 100) * (h - 4) - 2;
    const linePts = norm.map((p, i) => `${i === 0 ? 'M' : 'L'}${xs[i].toFixed(1)},${yAt(p.value).toFixed(1)}`);
    const lineD = linePts.join(' ');
    const lastX = xs[norm.length - 1];
    const areaPts = [`M0,${h}`, ...norm.map((p, i) => `L${xs[i].toFixed(1)},${yAt(p.value).toFixed(1)}`), `L${lastX.toFixed(1)},${h}`, 'Z'];
    return { lineD, areaD: areaPts.join(' ') };
  }, [norm]);

  const y35 = h - 2 - 0.35 * (h - 4);

  if (norm.length < 2) {
    return <div className="merit-sparkline-svg h-14 w-full min-h-[56px] rounded border border-ir-border bg-ir-bg md:min-h-[64px] md:h-16" />;
  }

  return (
    <svg
      className="merit-sparkline-svg h-14 w-full min-h-[56px] rounded border border-ir-border bg-[#0d1424] md:min-h-[64px] md:h-16"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y={y35} width={w} height={h - y35} />
        </clipPath>
      </defs>
      <line
        x1="0"
        y1={y35}
        x2={w}
        y2={y35}
        stroke="var(--accent-gold)"
        strokeWidth="0.75"
        vectorEffect="non-scaling-stroke"
        opacity={0.85}
      />
      <path d={areaD} fill="rgba(220, 38, 38, 0.12)" clipPath={`url(#${clipId})`} />
      <path
        d={lineD}
        fill="none"
        stroke="#22d3ee"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function ClockEmpty({ lastIngestUtc, isFa }: { lastIngestUtc?: number | null; isFa: boolean }) {
  const ingest =
    lastIngestUtc != null && Number.isFinite(lastIngestUtc)
      ? new Date(lastIngestUtc).toISOString()
      : '—';
  return (
    <div
      className="rounded border border-ir-border bg-ir-surface p-4 transition-colors hover:border-cyan-500/20"
      style={{ borderColor: 'var(--border)' }}
    >
      <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.12em] text-ir-muted">
        {isFa ? 'مدت قطع' : 'Outage duration'}
      </p>
      <p className="mt-2 font-sans text-sm text-ir-muted">
        {isFa
          ? 'نمونهٔ merit-nt در بازهٔ ۴۵ روز نیست.'
          : 'No merit-nt samples in the 45-day window.'}
      </p>
      <p className="mt-2 font-mono text-[10px] text-ir-muted">
        {isFa ? 'آخرین ingest: ' : 'Last ingest: '}
        {ingest}
      </p>
    </div>
  );
}

interface SinceThirtyFiveTimerProps {
  connectivityPoints: IODADataPoint[];
  secondaryPoints?: IODADataPoint[];
  currentPct: number;
  lookbackDays?: number;
  isFa: boolean;
  lastIngestUtc?: number | null;
}

function panelAccentClass(resolution: OutageClockResolution, currentPct: number): string {
  if (resolution?.mode === 'active_outage' || resolution?.mode === 'span_below') return 'clock-state-active';
  if (currentPct > 60) return 'clock-state-normal';
  return 'clock-state-recovery';
}

function durationColorClass(digitTw: string): string {
  if (digitTw.includes('ir-red')) return 'text-ir-red';
  if (digitTw.includes('ir-amber')) return 'text-ir-amber';
  if (digitTw.includes('ir-green')) return 'text-emerald-400';
  return 'text-slate-300';
}

const SinceThirtyFiveTimerInner: React.FC<SinceThirtyFiveTimerProps> = ({
  connectivityPoints,
  secondaryPoints,
  currentPct,
  lookbackDays: _lookbackDays = 45,
  isFa,
  lastIngestUtc,
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const resolution = useMemo(() => {
    if (connectivityPoints.length === 0) return null;
    return resolveOutageClock(connectivityPoints, secondaryPoints, currentPct);
  }, [connectivityPoints, secondaryPoints, currentPct]);

  const accentCls = resolution ? panelAccentClass(resolution, currentPct) : '';
  const digitCls = digitColorClass(currentPct);
  const durColor = durationColorClass(digitCls);

  const { d, h, m, s } = useMemo(() => {
    if (!resolution) return { d: 0, h: 0, m: 0, s: 0 };
    let durMs = 0;
    if (resolution.mode === 'active_outage') durMs = now - resolution.outageStartMs;
    else if (resolution.mode === 'recovery') durMs = now - resolution.lastGoodMs;
    else if (resolution.mode === 'span_below') durMs = now - resolution.firstSampleMs;
    return splitDuration(durMs);
  }, [now, resolution]);

  if (connectivityPoints.length === 0 || resolution == null) {
    return <ClockEmpty lastIngestUtc={lastIngestUtc} isFa={isFa} />;
  }

  const inlineDur = formatInlineDuration(d, h, m, s);

  const shellClass =
    'flex flex-col gap-4 overflow-hidden rounded border border-ir-border bg-ir-surface p-4 md:p-5 transition-[border-color,background-color] duration-150 hover:border-cyan-500/25 hover:bg-ir-elevated/10';

  /* —— Recovery —— */
  if (resolution.mode === 'recovery') {
    return (
      <div className={`${shellClass} ${accentCls} md:flex-row md:items-stretch md:gap-8`}>
        <div className="flex min-w-0 flex-col gap-4 md:max-w-[min(42%,22rem)] md:shrink-0">
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ir-amber">
              {isFa ? 'بهبود' : 'Recovery'}
            </p>
            <p className="mt-1 font-sans text-sm font-medium text-ir-text">
              {isFa ? `اتصال ≥ ${IDEAL_THRESHOLD}٪` : `Connectivity ≥ ${IDEAL_THRESHOLD}%`}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ir-muted">
              {isFa ? 'زمان از آخرین عبور از آستانه' : 'Time since last threshold pass'}
            </p>
            <p dir="ltr" className={`outage-duration-inline mt-1 ${durColor}`}>
              {inlineDur}
            </p>
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center self-stretch">
          <MeritSparkline points={connectivityPoints} />
        </div>
      </div>
    );
  }

  /* —— Active outage —— */
  if (resolution.mode === 'active_outage') {
    return (
      <div className={`${shellClass} ${accentCls} md:flex-row md:items-stretch md:gap-8`}>
        <div className="min-w-0 md:max-w-[min(42%,22rem)] md:shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ir-muted">
            {isFa ? 'مدت قطع جاری' : 'Ongoing outage duration'}
          </p>
          <p dir="ltr" className={`outage-duration-inline mt-1 ${durColor}`}>
            {inlineDur}
          </p>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center self-stretch">
          <MeritSparkline points={connectivityPoints} />
        </div>
      </div>
    );
  }

  /* —— Span fallback —— */
  if (resolution.mode === 'span_below') {
    const { maxPct } = resolution;
    return (
      <div className={`${shellClass} ${accentCls} md:flex-row md:items-stretch md:gap-8`}>
        <div className="flex min-w-0 flex-col gap-4 md:max-w-[min(42%,22rem)] md:shrink-0">
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ir-amber">
              {isFa ? 'پنجره زیر آستانه' : 'Full window below threshold'}
            </p>
            <p className="mt-1 font-sans text-sm text-ir-text">
              {isFa ? `${_lookbackDays} روز` : `Last ${_lookbackDays} days`}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ir-muted">
              {isFa ? 'مدت نمایش' : 'Elapsed (window)'}
            </p>
            <p dir="ltr" className={`outage-duration-inline mt-1 ${durColor}`}>
              {inlineDur}
            </p>
          </div>
          <p className="text-xs text-ir-muted">
            {isFa ? `بیشینه در پنجره: ${maxPct.toFixed(1)}٪` : `Peak in window: ${maxPct.toFixed(1)}%`}
          </p>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center self-stretch">
          <MeritSparkline points={connectivityPoints} />
        </div>
      </div>
    );
  }

  return <ClockEmpty lastIngestUtc={lastIngestUtc} isFa={isFa} />;
};

/** Props are stable across header tick; avoids parent-driven re-renders from unrelated dashboard state. */
export const SinceThirtyFiveTimer = React.memo(SinceThirtyFiveTimerInner);
